import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { useMemo } from "react";
import * as pc from "playcanvas";
import { TubeConnection } from "./node-components/tube-connection";
import { GripPosition } from "./node-components/grip-position";
import { CentralizedVacuumPump } from "./node-components/centralized-vacuum-pump";
import { Attachment } from "./node-components/attachment";
import { FeedPressurePoint } from "./node-components/feed-pressure-point";
import { NodeOverlay } from "./overlay-elements/node-overlay";
import { NodeWarning } from "./overlay-elements/node-warning";
import { NodeTooltip } from "./overlay-elements/tooltip-components/node-tooltip";
import { NodeVacuumLevel } from "./overlay-elements/node-vacuum-level";
import { NodeAlphabeticalId } from "./overlay-elements/node-alphabetical-id";
import { useAppSelector, selectors } from "store";
import { NodeComponentType, } from "store/types";
export var NodeUI = function (_a) {
    var app = _a.app, node = _a.node, nodeEntity = _a.nodeEntity, modelAssets = _a.modelAssets, materialAssets = _a.materialAssets, position = _a.position, boundingBox = _a.boundingBox, isSelected = _a.isSelected, isMoving = _a.isMoving, isZoomedIn = _a.isZoomedIn, isNoneSelected = _a.isNoneSelected, isMultiSelecting = _a.isMultiSelecting, hasWarnings = _a.hasWarnings, hoverId = _a.hoverId, hoveredNodeTooltipId = _a.hoveredNodeTooltipId, onTooltipHover = _a.onTooltipHover;
    var attachments = useAppSelector(selectors.selectAttachments);
    var component = useMemo(function () {
        var _a;
        if (node.type === NodeComponentType.GRIP_POSITION) {
            var gripPosition = node.component;
            var cameraEntity = app.root.findByName("Camera");
            var currentNodePosition = nodeEntity.getPosition();
            var edgePosition = new pc.Vec3(currentNodePosition.x + gripPosition.width / 2, currentNodePosition.y, currentNodePosition.z);
            var edgePositionScreen = (_a = cameraEntity.camera) === null || _a === void 0 ? void 0 : _a.worldToScreen(edgePosition);
            return (_jsx(GripPosition, { app: app, nodeEntity: nodeEntity, modelAssets: modelAssets, materialAssets: materialAssets, gripPosition: node.component, isMoving: isMoving, position: position, width: edgePositionScreen && position
                    ? edgePositionScreen.x - position.x
                    : undefined }));
        }
        else if (node.type === NodeComponentType.TUBE_CONNECTION) {
            return (_jsx(TubeConnection, { app: app, nodeEntity: nodeEntity, materialAssets: materialAssets, modelAssets: modelAssets, tubeConnection: node.component }));
        }
        else if (node.type === NodeComponentType.CENTRALIZED_VACUUM_PUMP) {
            return (_jsx(CentralizedVacuumPump, { app: app, nodeEntity: nodeEntity, materialAssets: materialAssets, modelAssets: modelAssets, node: node, isMoving: isMoving, position: position }));
        }
        else if (node.type === NodeComponentType.FEED_PRESSURE_POINT) {
            return (_jsx(FeedPressurePoint, { nodeEntity: nodeEntity, materialAssets: materialAssets, modelAssets: modelAssets, feedPressurePoint: node.component, node: node }));
        }
        else {
            return null;
        }
    }, [node, app, nodeEntity, modelAssets, materialAssets, isMoving, position]);
    var buttons = useMemo(function () {
        if (!(boundingBox === null || boundingBox === void 0 ? void 0 : boundingBox.height) || !(position === null || position === void 0 ? void 0 : position.x) || isMoving)
            return null;
        var buttonsCenterX = position.x;
        var buttonsCenterY = position.y;
        var buttonsTopY = boundingBox.center.y - boundingBox.height / 2;
        if (isZoomedIn) {
            buttonsCenterX = app.graphicsDevice.canvas.clientWidth / 2;
            buttonsCenterY = app.graphicsDevice.canvas.clientHeight / 2;
            buttonsTopY = app.graphicsDevice.canvas.clientHeight - 250;
        }
        return (_jsx(NodeOverlay, { isZoomedIn: isZoomedIn, nodeBoundingBoxHeight: boundingBox.height, nodeTopY: buttonsTopY, nodeCenterX: buttonsCenterX, nodeCenterY: buttonsCenterY, node: node }));
    }, [
        boundingBox === null || boundingBox === void 0 ? void 0 : boundingBox.height,
        boundingBox === null || boundingBox === void 0 ? void 0 : boundingBox.center.y,
        position === null || position === void 0 ? void 0 : position.x,
        position === null || position === void 0 ? void 0 : position.y,
        isMoving,
        isZoomedIn,
        node,
        app.graphicsDevice.canvas.clientWidth,
        app.graphicsDevice.canvas.clientHeight,
    ]);
    var warning = useMemo(function () {
        if (!hasWarnings || !(position === null || position === void 0 ? void 0 : position.x))
            return null;
        return _jsx(NodeWarning, { nodeCenterX: position.x, nodeCenterY: position.y });
    }, [hasWarnings, position === null || position === void 0 ? void 0 : position.x, position === null || position === void 0 ? void 0 : position.y]);
    var performanceTooltip = useMemo(function () {
        if (!(boundingBox === null || boundingBox === void 0 ? void 0 : boundingBox.width) || !(position === null || position === void 0 ? void 0 : position.y))
            return null;
        var tooltipCenterY = position.y;
        var tooltipRightX = boundingBox.center.x - boundingBox.width / 2;
        var tooltipLeftX = boundingBox.center.x + boundingBox.width / 2;
        if (isZoomedIn) {
            tooltipRightX = 10;
            tooltipLeftX = app.graphicsDevice.canvas.clientWidth - 10;
            tooltipCenterY = 120;
        }
        return (_jsx(NodeTooltip, { node: node, nodeCenterY: tooltipCenterY, nodeRightX: tooltipRightX, nodeLeftX: tooltipLeftX, isSelected: isSelected, isMoving: isMoving, onTooltipHover: onTooltipHover, hoveredNodeTooltipId: hoveredNodeTooltipId }));
    }, [
        boundingBox === null || boundingBox === void 0 ? void 0 : boundingBox.width,
        boundingBox === null || boundingBox === void 0 ? void 0 : boundingBox.center.x,
        position === null || position === void 0 ? void 0 : position.y,
        isZoomedIn,
        node,
        isSelected,
        isMoving,
        onTooltipHover,
        hoveredNodeTooltipId,
        app.graphicsDevice.canvas.clientWidth,
    ]);
    var vacuumLevel = useMemo(function () {
        if (!(position === null || position === void 0 ? void 0 : position.x) || !(position === null || position === void 0 ? void 0 : position.y))
            return null;
        return (_jsx(NodeVacuumLevel, { nodeId: node.id, nodeCenterX: position.x, nodeCenterY: position.y }));
    }, [position === null || position === void 0 ? void 0 : position.x, position === null || position === void 0 ? void 0 : position.y, node.id]);
    var alphabeticalIdOverlay = useMemo(function () {
        if (!(position === null || position === void 0 ? void 0 : position.x) || !(position === null || position === void 0 ? void 0 : position.y))
            return null;
        return (_jsx(NodeAlphabeticalId, { node: node, nodeCenterX: position.x, nodeCenterY: position.y }));
    }, [position === null || position === void 0 ? void 0 : position.x, position === null || position === void 0 ? void 0 : position.y, node]);
    return (_jsxs(_Fragment, { children: [performanceTooltip, isSelected && !isMultiSelecting && buttons, isNoneSelected && warning, vacuumLevel, alphabeticalIdOverlay, component, !isZoomedIn &&
                !isMultiSelecting &&
                node.attachmentIds.map(function (attachmentId) { return (_jsx(Attachment, { app: app, nodeEntity: nodeEntity, node: node, attachment: attachments[attachmentId], materialAssets: materialAssets, modelAssets: modelAssets, hoverId: hoverId }, attachmentId)); })] }));
};
